import { Carousel, type CarouselApi, CarouselContent } from '#/components/ui/carousel';
import { cn } from '#/lib/utils';
import { useCallback, useEffect, useState } from 'react';

import type { ServiceMenus } from '../api/global-config';
import { SvgIconArrowCarouselNext } from '../svg/icon-arrow-carousel-next';
import { SvgIconArrowCarouselPrev } from '../svg/icon-arrow-carousel-prev';
import { SiteFooterCarouselItem } from './site-footer-carousel-item';

export const SiteFooterCarousel = (props: { serviceMenus: ServiceMenus }) => {
  const { serviceMenus } = props;
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  // dots/arrow スライド変更時の処理
  const onSelect = useCallback(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());

    setPrevBtnDisabled(!api.canScrollPrev());
    setNextBtnDisabled(!api.canScrollNext());
  }, [api]);

  // dotsクリック時の処理
  const scrollTo = useCallback((index: number) => api && api.scrollTo(index), [api]);

  useEffect(() => {
    if (!api) return;
    onSelect();
    setScrollSnaps(api.scrollSnapList());
    api.on('select', onSelect);
    return () => {
      api.off('select', onSelect);
    };
  }, [api, onSelect]);

  const scrollPrev = useCallback(() => {
    if (api) api.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    if (api) api.scrollNext();
  }, [api]);

  return (
    <>
      <div className="relative mx-auto mb-20 px-4 max-lg:w-full md:px-4 lg:w-[1152px]">
        <div className="mb-6 overflow-x-hidden">
          <Carousel
            setApi={setApi}
            opts={{
              align: 'start',
            }}>
            <CarouselContent className="-ml-0 md:-ml-4">
              <SiteFooterCarouselItem serviceMenus={serviceMenus} />
            </CarouselContent>
          </Carousel>
          <div className="mt-6 flex justify-center gap-2 md:hidden">
            {scrollSnaps.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollTo(index)}
                className={cn(
                  'h-[6px] rounded-full transition-all',
                  current === index ? 'w-[26px] bg-white' : 'bg-red-850 w-[6px]'
                )}
                aria-label={`サービス ${index + 1}に移動`}
              />
            ))}
          </div>
        </div>
        <div className="absolute top-[88px] -left-2 mb-8 hidden justify-end md:flex">
          <button
            aria-label="Go to slide previous"
            className="opacity-100 outline-blue-500 transition disabled:pointer-events-none disabled:opacity-0"
            onClick={scrollPrev}
            disabled={prevBtnDisabled}>
            <SvgIconArrowCarouselPrev width={48} height={48} className="size-12 flex-none"></SvgIconArrowCarouselPrev>
          </button>
        </div>
        <div className="absolute top-[88px] -right-2 mb-8 hidden justify-end md:flex">
          <button
            aria-label="Go to slide next"
            className="opacity-100 outline-blue-500 transition disabled:pointer-events-none disabled:opacity-0"
            onClick={scrollNext}
            disabled={nextBtnDisabled}>
            <SvgIconArrowCarouselNext width={48} height={48} className="size-12 flex-none"></SvgIconArrowCarouselNext>
          </button>
        </div>
        <ul className="text-caption-s ml-[10px] list-outside list-['※'] space-y-1 place-self-start text-left text-white [&>li]:pl-2">
          <li>
            利用料金全額には付与されない場合があります。付与対象となる利用料の詳細と計算方法は
            <a
              href={`${import.meta.env.VITE_APP_KABUAND_URL}/documents/kabuhikikaeken-information.pdf`}
              className="inline underline underline-offset-2">
              こちら
            </a>
            。
          </li>
          <li>記載の価格は税抜記載のものを除き税込です。</li>
        </ul>
      </div>
    </>
  );
};
